import React, { useState } from 'react';
import { Mail, User, ArrowRight, Loader2, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AuthModal = ({ mode, onClose, onSuccess, onSwitchMode, onNavigate, initialEmail }) => {
  const [step, setStep] = useState(mode === 'login' ? 'email' : 'register'); // register, email, verify
  const [name, setName] = useState('');
  const [email, setEmail] = useState(initialEmail || '');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [userName, setUserName] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      await axios.post(`${API}/auth/register`, { name, email, accepted_terms: true, marketing_consent: true });
      setUserName(name);
      setStep('verify');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al registrar');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/auth/login`, { email });
      setUserName(response.data.name);
      setStep('verify');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/auth/verify`, { email, code });
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      onSuccess(response.data.user);
    } catch (err) {
      setError(err.response?.data?.detail || 'Código incorrecto');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    setError('');
    
    try {
      await axios.post(`${API}/auth/resend-code`, { email });
      setError('Nuevo código enviado');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al reenviar código');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="w-full max-w-md bg-[#0d0d0f] border border-gray-800 rounded-2xl p-6 sm:p-8 relative">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-white transition-colors"
        >
          ✕
        </button>

        {/* Logo */}
        <div className="text-center mb-8">
          <img src="/logo.png" alt="TraceMetrics" className="h-16 mx-auto mb-4" />
          <h2 className="text-2xl font-bold">
            {step === 'register' && 'Crear Cuenta'}
            {step === 'email' && 'Iniciar Sesión'}
            {step === 'verify' && 'Verificar Email'}
          </h2>
          <p className="text-gray-400 text-sm mt-2">
            {step === 'register' && 'Comienza gratis con 20 operaciones'}
            {step === 'email' && 'Te enviaremos un código de acceso'}
            {step === 'verify' && `Código enviado a ${email}`}
          </p>
        </div>

        {/* Error message */}
        {error && (
          <div className={`mb-4 p-3 rounded-lg text-sm ${error.includes('enviado') ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
            {error}
          </div>
        )}

        {/* Register Form */}
        {step === 'register' && (
          <form onSubmit={handleRegister} className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Nombre</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-[#0a0a0b] border border-gray-800 rounded-lg focus:outline-none focus:border-cyan-500 transition-colors"
                  placeholder="Tu nombre"
                  required
                  data-testid="register-name-input"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-[#0a0a0b] border border-gray-800 rounded-lg focus:outline-none focus:border-cyan-500 transition-colors"
                  placeholder="tu@email.com"
                  required
                  data-testid="register-email-input"
                />
              </div>
            </div>
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="accept-terms"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-1 w-4 h-4 rounded border-gray-600 bg-[#0a0a0b] text-cyan-500 focus:ring-cyan-500 focus:ring-offset-0 cursor-pointer accent-cyan-500"
                data-testid="accept-terms-checkbox"
              />
              <label htmlFor="accept-terms" className="text-gray-400 text-xs leading-relaxed cursor-pointer">
                He leído y acepto los{' '}
                <button
                  type="button"
                  onClick={() => onNavigate && onNavigate('terms')}
                  className="text-cyan-400 hover:underline"
                  data-testid="terms-link"
                >
                  Términos y Condiciones
                </button>
                {' '}y la{' '}
                <button
                  type="button"
                  onClick={() => onNavigate && onNavigate('privacy')}
                  className="text-cyan-400 hover:underline"
                  data-testid="privacy-link"
                >
                  Política de Privacidad
                </button>
                , incluyendo el tratamiento de mis datos personales y el envío de comunicaciones comerciales por parte de Noakai LLC.
              </label>
            </div>
            <button
              type="submit"
              disabled={loading || !acceptedTerms}
              className="w-full py-3 bg-gradient-to-r from-cyan-500 to-teal-500 rounded-lg font-semibold hover:from-cyan-400 hover:to-teal-400 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="register-submit-btn"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Continuar <ArrowRight className="w-5 h-5" /></>}
            </button>
            <p className="text-center text-gray-400 text-sm">
              ¿Ya tienes cuenta?{' '}
              <button
                type="button"
                onClick={() => { setStep('email'); setError(''); }}
                className="text-cyan-400 hover:underline"
              >
                Inicia sesión
              </button>
            </p>
          </form>
        )}

        {/* Login Form */}
        {step === 'email' && (
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-[#0a0a0b] border border-gray-800 rounded-lg focus:outline-none focus:border-cyan-500 transition-colors"
                  placeholder="tu@email.com"
                  required
                  data-testid="login-email-input"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-cyan-500 to-teal-500 rounded-lg font-semibold hover:from-cyan-400 hover:to-teal-400 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="login-submit-btn"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Enviar Código <ArrowRight className="w-5 h-5" /></>}
            </button>
            <p className="text-center text-gray-400 text-sm">
              ¿No tienes cuenta?{' '}
              <button
                type="button"
                onClick={() => { setStep('register'); setError(''); }}
                className="text-cyan-400 hover:underline"
              >
                Regístrate gratis
              </button>
            </p>
          </form>
        )}

        {/* Verify Form */}
        {step === 'verify' && (
          <form onSubmit={handleVerify} className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Código de verificación</label>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full px-4 py-4 bg-[#0a0a0b] border border-gray-800 rounded-lg focus:outline-none focus:border-cyan-500 transition-colors text-center text-2xl tracking-[0.5em] font-mono"
                placeholder="000000"
                maxLength={6}
                required
                data-testid="verify-code-input"
              />
            </div>
            <button
              type="submit"
              disabled={loading || code.length !== 6}
              className="w-full py-3 bg-gradient-to-r from-cyan-500 to-teal-500 rounded-lg font-semibold hover:from-cyan-400 hover:to-teal-400 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="verify-submit-btn"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Verificar <ArrowRight className="w-5 h-5" /></>}
            </button>
            <div className="flex items-center justify-between text-sm">
              <button
                type="button"
                onClick={() => setStep('email')}
                className="text-gray-400 hover:text-white flex items-center gap-1"
              >
                <ArrowLeft className="w-4 h-4" /> Cambiar email
              </button>
              <button
                type="button"
                onClick={handleResendCode}
                disabled={loading}
                className="text-cyan-400 hover:underline"
              >
                Reenviar código
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default AuthModal;
